<template>
  <div class="overview-page pa-3">
    <!-- 顶部综合打卡工作舱：选人与打卡胶囊合二为一 -->
    <v-card variant="flat" border class="pa-3 mb-3 rounded-xl bg-white">
      <!-- 选学员标题与展开收起切换 -->
      <div class="section-header-row mb-3 px-1">
        <div class="header-left">
          <v-icon color="primary" class="header-icon mr-1" size="small">mdi-account-group-outline</v-icon>
          <span class="header-title">学员选择</span>
          <span class="header-hint ml-1">
            {{ studentProgress > 0.5 ? '(右滑收起)' : '(左滑展开)' }}
          </span>
        </div>
        <div class="header-right">
          <v-chip
            v-if="currentStudent"
            size="x-small"
            color="teal"
            variant="tonal"
            class="header-chip mr-1"
          >
            {{ currentStudent.nick_name }} · 今日打卡 {{ kick_count || 0 }} 次
          </v-chip>
          <v-btn
            variant="text"
            size="x-small"
            density="compact"
            color="primary"
            class="header-btn"
            @click="toggleStudentExpand"
          >
            <span class="btn-text mr-0.5">{{ studentProgress > 0.5 ? '收起' : '更多' }}</span>
            <v-icon
              icon="mdi-chevron-down"
              size="14"
              class="chevron-icon"
              :style="{
                transform: `rotate(${studentProgress * 180}deg)`,
                transition: isStudentTouching ? 'none' : 'transform 0.28s ease'
              }"
            />
          </v-btn>
        </div>
      </div>

      <!-- 学员列表容器：手势跟手展开/收回，高度和第二排动态联动 -->
      <div
        class="student-slider-wrapper mb-1"
        :class="{ 'is-expanded': studentProgress > 0.5 }"
        :style="{
          height: studentContainerHeight,
          transition: isStudentTouching ? 'none' : 'height 0.28s cubic-bezier(0.25, 1, 0.5, 1)'
        }"
        @touchstart.passive="onStudentTouchStart"
        @touchmove="onStudentTouchMove"
        @touchend="onStudentTouchEnd"
        @touchcancel="onStudentTouchEnd"
      >
        <div
          class="student-grid-container"
          :class="{ 'allow-scroll': studentProgress === 1 && student_list.length > 15 }"
        >
          <div
            v-for="(item, index) in student_list"
            :key="item.ID"
            class="student-item text-center"
            :class="{
              'is-active': currentStudent && currentStudent.ID === item.ID
            }"
            :style="getStudentItemStyle(index)"
            @click="selectStudent(item)"
          >
            <!-- 头像槽位 -->
            <div class="avatar-slot mb-1">
              <div
                class="avatar-ring"
                :class="{ active: currentStudent && currentStudent.ID === item.ID }"
              >
                <v-avatar size="40" class="elevation-1 bg-grey-lighten-3">
                  <v-img :src="item.avatar" alt="avatar" cover>
                    <template v-slot:placeholder>
                      <div class="d-flex align-center justify-center fill-height">
                        <v-progress-circular indeterminate size="16" color="primary" />
                      </div>
                    </template>
                  </v-img>
                </v-avatar>
                <!-- 选中高亮绿底对勾徽标 -->
                <transition name="badge-pop">
                  <div
                    v-if="currentStudent && currentStudent.ID === item.ID"
                    class="selected-badge"
                  >
                    <v-icon icon="mdi-check" size="10" color="white" />
                  </div>
                </transition>
              </div>
            </div>
            <div
              class="text-caption text-truncate student-name"
              :class="currentStudent && currentStudent.ID === item.ID ? 'text-teal-darken-3 font-weight-black active-name' : 'text-grey-darken-1 font-weight-medium'"
              style="max-width: 100%;"
            >
              {{ item.nick_name }}
            </div>
          </div>
        </div>
      </div>

      <!-- 轻量分割线与打卡类型标题栏 -->
      <div class="section-header-row my-2 px-1">
        <div class="header-left">
          <v-icon color="teal" class="header-icon mr-1" size="small">mdi-checkbox-marked-circle-outline</v-icon>
          <span class="header-title">打卡类型</span>
          <span class="header-hint ml-1">
            {{ statusProgress > 0.5 ? '(右滑收起)' : '(左滑展开)' }}
          </span>
        </div>
        <div class="header-right">
          <v-btn
            variant="text"
            size="x-small"
            density="compact"
            color="teal-darken-1"
            class="header-btn"
            @click="toggleStatusExpand"
          >
            <span class="btn-text mr-0.5">{{ statusProgress > 0.5 ? '收起' : '更多' }}</span>
            <v-icon
              icon="mdi-chevron-down"
              size="14"
              class="chevron-icon"
              :style="{
                transform: `rotate(${statusProgress * 180}deg)`,
                transition: isStatusTouching ? 'none' : 'transform 0.28s ease'
              }"
            />
          </v-btn>
        </div>
      </div>

      <!-- 打卡胶囊：手势跟手展开/收回，第二排渐变展示与回缩 -->
      <div
        class="pill-slider-wrapper"
        :class="{ 'is-expanded': statusProgress > 0.5 }"
        :style="{
          height: statusContainerHeight,
          transition: isStatusTouching ? 'none' : 'height 0.28s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.25s ease'
        }"
        @touchstart.passive="onStatusTouchStart"
        @touchmove="onStatusTouchMove"
        @touchend="onStatusTouchEnd"
        @touchcancel="onStatusTouchEnd"
      >
        <div class="pill-slider-grid px-1 py-1">
          <v-btn
            v-for="(item, index) in statusList"
            :key="item.id"
            rounded="pill"
            height="38"
            :color="item.color"
            variant="tonal"
            :loading="loading && currentAction === item.id"
            :disabled="submitting"
            class="pill-grid-btn font-weight-bold"
            :style="getStatusItemStyle(index)"
            @click="signIn(item.id)"
          >
            <v-icon
              start
              :icon="item.icon"
              size="14"
              class="mr-1"
            />
            <span class="pill-btn-text">{{ item.name }}</span>
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 下部：当前选中学员今日打卡明细 -->
    <v-card variant="flat" border class="pa-3 mb-4 rounded-xl bg-white" v-if="currentStudent">
      <div class="d-flex align-center mb-2 px-2 text-subtitle-2 text-grey-darken-2 font-weight-bold">
        <v-icon icon="mdi-history" size="small" class="mr-1" color="primary" />
        今日打卡
      </div>

      <div v-if="signData.length === 0" class="text-center py-6 text-grey">
        <v-icon icon="mdi-clipboard-text-outline" size="large" class="mb-1" />
        <div class="text-caption">暂无打卡记录</div>
      </div>

      <v-timeline v-else side="end" density="compact" class="px-2 sign-timeline">
        <v-timeline-item
          v-for="n in signData"
          :key="n.id || n.created_at"
          :dot-color="colorFilter(n.status)"
          size="x-small"
        >
          <div
            class="timeline-record-row d-flex align-center justify-space-between w-100 py-1"
            @click="openRecordDetail(n)"
          >
            <div class="d-flex align-center mr-2 text-truncate" style="min-width: 0;">
              <span class="text-body-2 font-weight-bold mr-2 flex-shrink-0">{{ formatTime(n.created_at) }}</span>
              <span class="text-caption text-grey-darken-1 font-weight-medium text-truncate">{{ n.teacher_name }}</span>
            </div>
            <div class="d-flex align-center flex-shrink-0">
              <v-chip size="small" :color="colorFilter(n.status)" variant="tonal" class="font-weight-medium mr-1">
                {{ statusText(n.status) }}
              </v-chip>
              <v-icon icon="mdi-chevron-right" size="14" color="grey-lighten-1" />
            </div>
          </div>
        </v-timeline-item>
      </v-timeline>
    </v-card>

    <!-- 今日流水明细与撤销弹窗 -->
    <v-dialog v-model="recordDialog" max-width="320">
      <v-card class="rounded-xl overflow-hidden" v-if="selectedRecord">
        <v-toolbar :color="colorFilter(selectedRecord.status)" dark density="compact">
          <v-toolbar-title class="text-subtitle-2 font-weight-bold">
            {{ statusText(selectedRecord.status) }}
          </v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pa-4">
          <div class="d-flex align-center mb-2">
            <v-icon icon="mdi-account" size="small" class="mr-2 text-grey" />
            <span class="text-caption text-grey-darken-1 mr-1">学员：</span>
            <span class="font-weight-bold text-body-2">{{ currentStudent ? currentStudent.nick_name : "" }}</span>
          </div>

          <div class="d-flex align-center mb-2">
            <v-icon icon="mdi-account-tie" size="small" class="mr-2 text-grey" />
            <span class="text-caption text-grey-darken-1 mr-1">打卡教师：</span>
            <span class="font-weight-bold text-body-2">{{ selectedRecord.teacher_name }}</span>
            <v-chip
              v-if="selectedRecord.user_id === user_id"
              size="x-small"
              color="teal"
              variant="tonal"
              class="ml-2 font-weight-bold"
            >
              我的打卡
            </v-chip>
          </div>

          <div class="d-flex align-center text-caption text-grey-darken-1">
            <v-icon icon="mdi-clock-outline" size="small" class="mr-2 text-grey" />
            <span class="mr-1">打卡时间：</span>
            <span class="font-weight-medium">{{ formatFullTime(selectedRecord.created_at) }}</span>
          </div>

          <div
            v-if="selectedRecord.user_id !== user_id"
            class="text-caption text-grey-darken-1 bg-grey-lighten-4 rounded-lg px-2 py-1 mt-3 text-center"
          >
            非本人打卡不可撤销
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <div v-if="selectedRecord.user_id === user_id" class="d-flex justify-space-between align-center w-100">
            <v-btn variant="text" size="small" @click="recordDialog = false">取消</v-btn>
            <v-btn
              color="error"
              variant="tonal"
              size="small"
              class="rounded-pill px-4 font-weight-bold"
              :loading="revoking"
              @click="revokeSelectedRecord"
            >
              <v-icon start icon="mdi-undo-variant" size="small" /> 撤销打卡
            </v-btn>
          </div>
          <v-btn
            v-else
            block
            variant="tonal"
            color="grey-darken-1"
            size="small"
            class="rounded-pill"
            @click="recordDialog = false"
          >
            关闭
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 反馈提示条 (支持撤销) -->
    <v-snackbar
      v-model="snackbar"
      location="bottom"
      :color="msgColor || 'success'"
      :timeout="timeout"
      rounded="pill"
      elevation="4"
      class="floating-toast"
    >
      <div class="d-flex align-center">
        <v-icon
          :icon="msgColor === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle'"
          size="18"
          class="mr-2 text-white"
        />
        <span class="text-body-2 font-weight-medium text-white">{{ text }}</span>
      </div>
      <template v-slot:actions>
        <v-btn
          v-if="signData.length > 0"
          variant="tonal"
          rounded="pill"
          size="small"
          color="white"
          class="px-3 font-weight-bold ml-1"
          @click="revoke"
        >
          <v-icon start icon="mdi-undo-variant" size="14" /> 撤销
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { studentList, getRecord, createRecord, deleteRecord } from "@/api";
import { statusFilter, statusTransfer } from "@/utils";
import moment from "moment";

export default {
  name: "Overview",
  data() {
    return {
      snackbar: false,
      text: "",
      timeout: 8000,
      msgColor: "",
      loading: false,
      submitting: false,
      currentAction: null,
      student_list: [],
      currentStudent: null,
      signData: [],
      kick_count: 0,
      user_id: parseInt(localStorage.getItem("id") || "0"),
      baseStatusList: [
        { id: 1, name: "个训", color: "success", icon: "mdi-account" },
        { id: 2, name: "集体", color: "teal", icon: "mdi-account-multiple" },
        { id: 7, name: "督导", color: "deep-purple", icon: "mdi-account-tie" },
        { id: 3, name: "影子", color: "indigo", icon: "mdi-bee" },
        { id: 4, name: "辅课", color: "amber-darken-2", icon: "mdi-account-heart" },
        { id: 0, name: "缺席", color: "error", icon: "mdi-close-circle-outline" },
        { id: 5, name: "20MIN", color: "blue-grey", icon: "mdi-clock-outline" },
        { id: 6, name: "30MIN", color: "brown", icon: "mdi-clock-time-four-outline" },
      ],
      statusList: [],
      recordDialog: false,
      selectedRecord: null,
      revoking: false,
      studentProgress: 0,
      statusProgress: 0,
      isStudentTouching: false,
      isStatusTouching: false,
      studentTouchStartX: 0,
      studentTouchStartY: 0,
      studentStartProgress: 0,
      isStudentHorizontalDrag: null,
      statusTouchStartX: 0,
      statusTouchStartY: 0,
      statusStartProgress: 0,
      isStatusHorizontalDrag: null,
    };
  },
  computed: {
    studentContainerHeight() {
      const minH = 82;
      let maxH = 82;
      if (this.student_list.length > 10) {
        maxH = 246;
      } else if (this.student_list.length > 5) {
        maxH = 164;
      }
      return `${minH + (maxH - minH) * this.studentProgress}px`;
    },
    statusContainerHeight() {
      const minH = 46;
      const maxH = 92;
      return `${minH + (maxH - minH) * this.statusProgress}px`;
    },
  },
  mounted() {
    this.statusList = [...this.baseStatusList];
    this.getStudentList();
  },
  methods: {
    getStudentItemStyle(index) {
      if (index < 5) return {};
      const p = this.studentProgress;
      return {
        opacity: p,
        transform: `translateY(${(1 - p) * 16}px)`,
        pointerEvents: p > 0.6 ? "auto" : "none",
        transition: this.isStudentTouching
          ? "none"
          : "opacity 0.28s cubic-bezier(0.25, 1, 0.5, 1), transform 0.28s cubic-bezier(0.25, 1, 0.5, 1)",
      };
    },
    getStatusItemStyle(index) {
      if (index < 4) return {};
      const p = this.statusProgress;
      return {
        opacity: p,
        transform: `translateY(${(1 - p) * 14}px)`,
        pointerEvents: p > 0.6 ? "auto" : "none",
        transition: this.isStatusTouching
          ? "none"
          : "opacity 0.28s cubic-bezier(0.25, 1, 0.5, 1), transform 0.28s cubic-bezier(0.25, 1, 0.5, 1)",
      };
    },
    toggleStudentExpand() {
      this.isStudentTouching = false;
      this.studentProgress = this.studentProgress > 0.5 ? 0 : 1;
    },
    toggleStatusExpand() {
      this.isStatusTouching = false;
      this.statusProgress = this.statusProgress > 0.5 ? 0 : 1;
    },
    onStudentTouchStart(e) {
      if (!e.touches || !e.touches[0]) return;
      this.studentTouchStartX = e.touches[0].clientX;
      this.studentTouchStartY = e.touches[0].clientY;
      this.studentStartProgress = this.studentProgress;
      this.isStudentTouching = true;
      this.isStudentHorizontalDrag = null;
    },
    onStudentTouchMove(e) {
      if (!this.isStudentTouching || !e.touches || !e.touches[0]) return;
      const deltaX = e.touches[0].clientX - this.studentTouchStartX;
      const deltaY = e.touches[0].clientY - this.studentTouchStartY;
      if (this.isStudentHorizontalDrag === null) {
        if (Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY) * 1.1) {
          this.isStudentHorizontalDrag = true;
        } else if (Math.abs(deltaY) > 6) {
          this.isStudentHorizontalDrag = false;
        }
      }
      if (this.isStudentHorizontalDrag) {
        if (e.cancelable) e.preventDefault();
        const dragDistance = 90;
        const nextProgress = this.studentStartProgress - deltaX / dragDistance;
        this.studentProgress = Math.max(0, Math.min(1, nextProgress));
      }
    },
    onStudentTouchEnd() {
      if (!this.isStudentTouching) return;
      this.isStudentTouching = false;
      this.studentProgress = this.studentProgress >= 0.4 ? 1 : 0;
      this.isStudentHorizontalDrag = null;
    },
    onStatusTouchStart(e) {
      if (!e.touches || !e.touches[0]) return;
      this.statusTouchStartX = e.touches[0].clientX;
      this.statusTouchStartY = e.touches[0].clientY;
      this.statusStartProgress = this.statusProgress;
      this.isStatusTouching = true;
      this.isStatusHorizontalDrag = null;
    },
    onStatusTouchMove(e) {
      if (!this.isStatusTouching || !e.touches || !e.touches[0]) return;
      const deltaX = e.touches[0].clientX - this.statusTouchStartX;
      const deltaY = e.touches[0].clientY - this.statusTouchStartY;
      if (this.isStatusHorizontalDrag === null) {
        if (Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY) * 1.1) {
          this.isStatusHorizontalDrag = true;
        } else if (Math.abs(deltaY) > 6) {
          this.isStatusHorizontalDrag = false;
        }
      }
      if (this.isStatusHorizontalDrag) {
        if (e.cancelable) e.preventDefault();
        const dragDistance = 85;
        const nextProgress = this.statusStartProgress - deltaX / dragDistance;
        this.statusProgress = Math.max(0, Math.min(1, nextProgress));
      }
    },
    onStatusTouchEnd() {
      if (!this.isStatusTouching) return;
      this.isStatusTouching = false;
      this.statusProgress = this.statusProgress >= 0.4 ? 1 : 0;
      this.isStatusHorizontalDrag = null;
    },
    getSortedStatusList(studentId) {
      if (!studentId) return [...this.baseStatusList];
      try {
        const storageKey = `sign_pref_${this.user_id}`;
        const prefData = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const studentPref = prefData[studentId] || {};
        return [...this.baseStatusList].sort((a, b) => {
          const countA = studentPref[a.id] || 0;
          const countB = studentPref[b.id] || 0;
          if (countB !== countA) {
            return countB - countA;
          }
          return (
            this.baseStatusList.findIndex((item) => item.id === a.id) -
            this.baseStatusList.findIndex((item) => item.id === b.id)
          );
        });
      } catch (e) {
        return [...this.baseStatusList];
      }
    },
    recordActionUsage(studentId, actionId) {
      if (!studentId || !actionId) return;
      try {
        const storageKey = `sign_pref_${this.user_id}`;
        const data = JSON.parse(localStorage.getItem(storageKey) || "{}");
        if (!data[studentId]) data[studentId] = {};
        data[studentId][actionId] = (data[studentId][actionId] || 0) + 1;
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch (e) {
        console.error("Save sign preference error", e);
      }
    },
    colorFilter(status) {
      return statusFilter(status);
    },
    statusText(status) {
      return statusTransfer(status) || "打卡";
    },
    formatTime(utcTime) {
      return moment(utcTime * 1000).format("HH:mm");
    },
    getStudentList() {
      studentList({}).then((res) => {
        this.student_list = res.data.data || [];
        if (this.student_list.length > 0) {
          // 默认选中第 1 位学生
          this.selectStudent(this.student_list[0]);
        }
      });
    },
    selectStudent(student) {
      this.currentStudent = student;
      this.statusList = this.getSortedStatusList(student.ID);
      this.getRecordByStudentId(student.ID);
    },
    getRecordByStudentId(studentId) {
      if (!studentId) return;
      const startDate = moment().format("YYYY-MM-DD 00:00:00");
      const end_at = moment().endOf("day").format("YYYY-MM-DD HH:mm:ss.SSS");
      getRecord({
        student_id: parseInt(studentId),
        start_at: startDate,
        end_at: end_at,
      }).then((res) => {
        this.signData = res.data?.data?.records || [];
        this.kick_count = this.signData.length;
      });
    },
    signIn(action) {
      if (!this.currentStudent) return;
      this.loading = true;
      this.currentAction = action;
      createRecord({
        status: action,
        student_id: this.currentStudent.ID,
        user_id: this.user_id,
      })
        .then((res) => {
          if (res.data.code == "200" || res.data.code == 200) {
            this.recordActionUsage(this.currentStudent.ID, action);
            this.statusList = this.getSortedStatusList(this.currentStudent.ID);
          } else {
            this.snackbar = true;
            this.text = res.data.msg || "打卡失败";
            this.msgColor = "error";
          }
          this.loading = false;
          this.currentAction = null;
          this.getRecordByStudentId(this.currentStudent.ID);
        })
        .catch(() => {
          this.loading = false;
          this.currentAction = null;
        });
    },
    formatFullTime(utcTime) {
      if (!utcTime) return "";
      return moment(utcTime * 1000).format("YYYY-MM-DD HH:mm:ss");
    },
    openRecordDetail(record) {
      this.selectedRecord = record;
      this.recordDialog = true;
    },
    revokeSelectedRecord() {
      if (!this.selectedRecord) return;
      if (this.selectedRecord.user_id !== this.user_id) {
        this.snackbar = true;
        this.text = "不能撤销其他教师的打卡记录";
        this.msgColor = "error";
        this.recordDialog = false;
        return;
      }
      this.revoking = true;
      deleteRecord({ id: this.selectedRecord.id })
        .then((res) => {
          this.revoking = false;
          this.recordDialog = false;
          if (res.data.code == "200" || res.data.code == 200) {
            this.snackbar = true;
            this.text = "打卡已撤销";
            this.msgColor = "success";
            this.getRecordByStudentId(this.currentStudent.ID);
          } else {
            this.snackbar = true;
            this.text = res.data.msg || "撤销失败";
            this.msgColor = "error";
          }
        })
        .catch(() => {
          this.revoking = false;
          this.snackbar = true;
          this.text = "网络异常，撤销失败";
          this.msgColor = "error";
        });
    },
    revoke() {
      if (!this.signData || this.signData.length === 0) return;
      deleteRecord({ id: this.signData.slice(-1)[0].id }).then((res) => {
        this.snackbar = false;
        if (res.data.code == "200" || res.data.code == 200) {
          this.getRecordByStudentId(this.currentStudent.ID);
        }
      });
    },
  },
};
</script>

<style scoped>
.overview-page {
  background-color: #f8fafc;
  max-width: 600px;
  margin: 0 auto;
}

/* 统一标题行与像素级垂直居中对齐 */
.section-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 26px;
  line-height: 1;
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.header-title {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.header-hint {
  font-size: 11px;
  color: #94a3b8;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  user-select: none;
}

.header-chip {
  font-weight: 700;
  height: 22px !important;
  font-size: 11px !important;
  padding: 0 6px !important;
  display: inline-flex !important;
  align-items: center !important;
  line-height: 1 !important;
  margin: 0 !important;
}

.header-btn {
  font-weight: 700 !important;
  height: 22px !important;
  min-height: 22px !important;
  padding: 0 2px !important;
  display: inline-flex !important;
  align-items: center !important;
  line-height: 1 !important;
}

.header-btn :deep(.v-btn__content) {
  display: inline-flex;
  align-items: center;
  line-height: 1;
  height: 100%;
}

.btn-text {
  font-size: 12px;
  line-height: 1;
  display: inline-block;
}

.chevron-icon {
  display: inline-flex;
  align-items: center;
}

/* 学员网格容器：5列排布 */
.student-slider-wrapper {
  overflow: hidden;
  position: relative;
  touch-action: pan-y;
}

.student-grid-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px 4px;
  padding: 10px 2px 4px 2px;
  width: 100%;
}

.student-grid-container.allow-scroll {
  max-height: 246px;
  overflow-y: auto;
}

.student-item {
  position: relative;
  cursor: pointer;
  opacity: 0.65;
  transition: opacity 0.25s ease;
  will-change: transform, opacity;
  width: 100%;
}

.student-item.is-active {
  opacity: 1 !important;
  z-index: 5;
}

.student-item:active .avatar-ring {
  transform: scale(0.95);
  transition-duration: 0.1s;
}

.student-item.is-active:active .avatar-ring {
  transform: scale(1.10);
  transition-duration: 0.1s;
}

.avatar-slot {
  width: 44px;
  height: 44px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.avatar-ring {
  position: relative;
  padding: 0;
  border-radius: 50%;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-ring.active {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 3.5px #00897b, 0 6px 14px -1px rgba(0, 137, 123, 0.4);
  transform: scale(1.18);
}

.selected-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #00897b;
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  z-index: 4;
}

.badge-pop-enter-active,
.badge-pop-leave-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.badge-pop-enter-from,
.badge-pop-leave-to {
  transform: scale(0);
  opacity: 0;
}

.student-name {
  transition: color 0.25s ease, font-weight 0.25s ease, transform 0.25s ease;
  font-size: 11px;
  margin-top: 2px;
  line-height: 1.3;
}

.student-name.active-name {
  color: #004d40 !important;
  font-weight: 800 !important;
  transform: scale(1.06);
}

/* 课程打卡胶囊滑块包装器 (默认4列，展开为4+4两排) */
.pill-slider-wrapper {
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  touch-action: pan-y;
}

.pill-slider-wrapper.is-expanded {
  background: #ffffff;
  border-color: rgba(0, 137, 123, 0.18);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.pill-slider-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px 6px;
  width: 100%;
}

.pill-grid-btn {
  width: 100% !important;
  min-width: 0 !important;
  padding: 0 4px !important;
  margin: 0 !important;
  font-size: 12px !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
  will-change: transform, opacity;
}

.pill-grid-btn :deep(.v-btn__content) {
  width: 100%;
  justify-content: center;
}

.pill-btn-text {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sign-timeline :deep(.v-timeline-item__body) {
  width: 100% !important;
  justify-self: stretch !important;
  min-width: 0 !important;
}

.timeline-record-row {
  cursor: pointer;
  border-radius: 8px;
  padding: 4px 6px;
  transition: background-color 0.15s ease;
}

.timeline-record-row:active {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
